import { faArrowRight, faCode, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import projectsData from '../data/projects.json';

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

export default function MostRecentProjects() {
    const navigate = useNavigate();
    const [hoverIndex, setHoverIndex] = useState(null);
    
    // Refs for animation targets
    const sectionRef = useRef(null);
    const headingRef = useRef(null);
    const descriptionRef = useRef(null);
    const projectsRef = useRef(null);
    const buttonRef = useRef(null);
    const projectCardsRef = useRef([]);

    // Get only featured projects or first 3 projects
    const featuredProjects = useMemo(() => {
        const featured = projectsData.filter(project => project.featured);
        return featured.length ? featured : projectsData.slice(0, 3);
    }, []);

    // Set up animations
    useEffect(() => {
        // Reset card refs array
        projectCardsRef.current = [];
        
        // Main section fade in
        gsap.fromTo(
            sectionRef.current,
            { opacity: 0 },
            { 
                opacity: 1, 
                duration: 1,
                ease: "power2.out",
            }
        );

        // Section heading animation
        gsap.fromTo(
            headingRef.current,
            { y: 50, opacity: 0 },
            { 
                y: 0, 
                opacity: 1, 
                duration: 1,
                ease: "back.out(1.7)",
                scrollTrigger: {
                    trigger: headingRef.current,
                    start: "top bottom-=100",
                    toggleActions: "play none none none"
                }
            }
        );

        // Description fade in
        gsap.fromTo(
            descriptionRef.current,
            { opacity: 0 },
            { 
                opacity: 1, 
                duration: 1,
                delay: 0.3,
                ease: "power2.out",
                scrollTrigger: {
                    trigger: descriptionRef.current,
                    start: "top bottom-=100",
                    toggleActions: "play none none none"
                }
            }
        );

        // Staggered animation for project cards
        gsap.fromTo(
            projectCardsRef.current,
            { 
                y: 100, 
                opacity: 0,
                scale: 0.9,
            },
            { 
                y: 0, 
                opacity: 1,
                scale: 1,
                duration: 0.8,
                stagger: 0.2,
                ease: "back.out(1.7)",
                scrollTrigger: {
                    trigger: projectsRef.current,
                    start: "top bottom-=50",
                    toggleActions: "play none none none"
                }
            }
        );

        // Button animation
        gsap.fromTo(
            buttonRef.current,
            { 
                y: 30, 
                opacity: 0,
            },
            { 
                y: 0, 
                opacity: 1,
                duration: 0.8,
                ease: "back.out(1.7)",
                scrollTrigger: {
                    trigger: buttonRef.current,
                    start: "top bottom-=50",
                    toggleActions: "play none none none"
                }
            }
        );
    }, []);

    // Add each project card ref to the array
    const addToRefs = (el) => {
        if (el && !projectCardsRef.current.includes(el)) {
            projectCardsRef.current.push(el);
        }
    };

    return (
        <section 
            ref={sectionRef}
            className="py-24 px-4 relative overflow-hidden bg-white dark:bg-gray-900 transition-colors duration-300"
            id="recent-projects"
        >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#8f00ff]/5 dark:bg-[#8f00ff]/10 rounded-full -translate-y-1/3 translate-x-1/3 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#ffd700]/5 dark:bg-[#ffd700]/10 rounded-full translate-y-1/3 -translate-x-1/3 blur-3xl"></div>
            
            <div className="container mx-auto max-w-6xl relative z-10">
                <div className="flex flex-col items-center text-center mb-16">
                    <span className="bg-gradient-to-r from-[#8f00ff]/10 to-[#ffd700]/10 dark:from-[#8f00ff]/20 dark:to-[#ffd700]/20 text-[#8f00ff] dark:text-[#ffd700] px-4 py-1.5 rounded-full text-sm font-medium tracking-wide mb-3">MY WORK</span>
                    <div ref={headingRef}>
                        <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300">
                            Recent Projects
                        </h2>
                        <div className="w-24 h-1.5 bg-gradient-to-r from-[#8f00ff] to-[#ffd700] rounded-full mb-6 mx-auto"></div>
                    </div>
                    <p ref={descriptionRef} className="text-gray-600 dark:text-gray-400 max-w-2xl text-lg">
                        Check out some of my latest web, mobile, and software projects that showcase my skills and passion.
                    </p>
                </div>
                
                <div ref={projectsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {featuredProjects.map((project, index) => (
                        <div 
                            key={project.id}
                            ref={addToRefs}
                            className="project-card group relative flex flex-col h-full"
                            onMouseEnter={() => setHoverIndex(index)}
                            onMouseLeave={() => setHoverIndex(null)}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-[#8f00ff]/5 to-[#ffd700]/5 dark:from-[#8f00ff]/10 dark:to-[#ffd700]/10 rounded-2xl transform rotate-1 translate-x-1 translate-y-1 group-hover:rotate-2 group-hover:translate-x-2 group-hover:translate-y-2 transition-all duration-500"></div>
                            
                            <div className="relative bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col h-full border border-gray-100 dark:border-gray-700 backdrop-blur-sm">
                                <div className="relative overflow-hidden h-52">
                                    <img
                                        src={project.imageUrl}
                                        alt={project.title}
                                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-in-out"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = `https://via.placeholder.com/600x400?text=${project.title.replace(/ /g, '+')}`;
                                        }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                    
                                    {/* Project links */}
                                    <div className={`absolute bottom-0 w-full p-4 flex items-center justify-between transform ${hoverIndex === index ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'} transition-all duration-500 ease-out`}>
                                        <div className="flex space-x-2">
                                            <a 
                                                href={project.demoUrl} 
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm text-[#8f00ff] hover:bg-[#8f00ff] hover:text-white transition-all duration-300 shadow-lg"
                                                aria-label="View live demo"
                                            >
                                                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-sm" />
                                            </a>
                                            <a 
                                                href={project.codeUrl} 
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-center w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm text-[#8f00ff] hover:bg-[#8f00ff] hover:text-white transition-all duration-300 shadow-lg"
                                                aria-label="View source code"
                                            >
                                                <FontAwesomeIcon icon={faCode} className="text-sm" />
                                            </a>
                                        </div>
                                        
                                        {/* Year and category badge */}
                                        <span className="text-xs text-white font-medium tracking-wide opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                                            {2025} • {project.category}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="flex-grow p-6 flex flex-col">
                                    <h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-white group-hover:text-[#8f00ff] dark:group-hover:text-[#ffd700] transition-colors duration-300">
                                        {project.title}
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-400 mb-5 flex-grow text-sm leading-relaxed">
                                        {project.description}
                                    </p>
                                    
                                    <div className="flex flex-wrap gap-2 mt-auto">
                                        {project.tech.map((tech, i) => (
                                            <span 
                                                key={i}
                                                className="px-3 py-1 text-xs font-medium rounded-full
                                                        bg-gradient-to-r from-[#8f00ff]/5 to-[#ffd700]/5
                                                        dark:from-[#8f00ff]/10 dark:to-[#ffd700]/10
                                                        text-gray-700 dark:text-gray-300
                                                        border border-[#8f00ff]/10 dark:border-[#ffd700]/10"
                                            >
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                
                <div className="mt-16 text-center">
                    <button 
                        ref={buttonRef}
                        onClick={() => navigate('/projects')}
                        className="group relative inline-flex items-center gap-2 px-7 py-3.5 rounded-full overflow-hidden bg-white dark:bg-gray-800 border border-[#8f00ff]/20 dark:border-[#ffd700]/20 shadow-md hover:shadow-xl transition-all duration-300"
                    >
                        <span className="absolute inset-0 bg-gradient-to-r from-[#8f00ff] to-[#ffd700] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
                        <span className="relative z-10 text-base font-medium text-gray-800 dark:text-white group-hover:text-white transition-colors duration-300">
                            View all projects
                        </span>
                        <FontAwesomeIcon 
                            icon={faArrowRight} 
                            className="relative z-10 text-[#8f00ff] dark:text-[#ffd700] group-hover:text-white transform group-hover:translate-x-1 transition-all duration-300"
                        />
                    </button>
                </div>
            </div>
        </section>
    );
}